import React from 'react';
import { ChevronLeftIcon, ClockIcon, UserIcon, EyeIcon, PaperclipIcon, TagIcon, DownloadIcon, MessageSquareIcon, FileIcon } from 'lucide-react';
import { Note, NoteHistoryEntry } from './NotesManagement';
import { StatusBadge } from '../../StatusBadge';
interface NoteDetailProps {
  note: Note;
  history: NoteHistoryEntry[];
  onBack: () => void;
  locale: string;
}
export function NoteDetail({
  note,
  history,
  onBack,
  locale
}: NoteDetailProps) {
  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Render visibility badge
  const renderVisibilityBadge = (visibility: string) => {
    switch (visibility) {
      case 'Public':
        return <StatusBadge status="success" text="Public" data-id="element-18773" />;
      case 'Role-based':
        return <StatusBadge status="warning" text="Role-based" data-id="element-18774" />;
      case 'User-specific':
        return <StatusBadge status="info" text="User-specific" data-id="element-18775" />;
      case 'Creator-only':
        return <StatusBadge status="error" text="Private" data-id="element-18776" />;
      default:
        return <StatusBadge status="default" text={visibility} data-id="element-18777" />;
    }
  };
  // Render history action badge
  const renderActionBadge = (action: string) => {
    switch (action) {
      case 'Created':
        return <StatusBadge status="success" text="Created" data-id="element-18778" />;
      case 'Modified':
        return <StatusBadge status="warning" text="Modified" data-id="element-18779" />;
      case 'Visibility Changed':
        return <StatusBadge status="info" text="Visibility Changed" data-id="element-18780" />;
      case 'Attachment Added':
        return <StatusBadge status="success" text="Attachment Added" data-id="element-18781" />;
      case 'Attachment Removed':
        return <StatusBadge status="error" text="Attachment Removed" data-id="element-18782" />;
      default:
        return <StatusBadge status="default" text={action} data-id="element-18783" />;
    }
  };
  return <div className="space-y-6" data-id="element-18784">
      {/* Back button */}
      <div data-id="element-18785">
        <button onClick={onBack} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18786">
          <ChevronLeftIcon className="h-4 w-4 mr-2" data-id="element-18787" />
          Back to Notes
        </button>
      </div>
      {/* Note details card */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-18788">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-start" data-id="element-18789">
          <div data-id="element-18790">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-18791">
              Note Details
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-18792">
              Full information and history for this note.
            </p>
          </div>
          <div className="flex items-center" data-id="element-18793">
            <span className="mr-2 text-sm text-gray-500" data-id="element-18794">
              {formatTimestamp(note.createdAt)}
            </span>
            {renderVisibilityBadge(note.visibility)}
          </div>
        </div>
        {/* Module reference */}
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-18795">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2" data-id="element-18796">
            <div className="sm:col-span-1" data-id="element-18797">
              <dt className="text-sm font-medium text-gray-500" data-id="element-18798">
                Module Reference
              </dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-18799">
                <span className="font-medium text-indigo-600" data-id="element-18800">
                  {note.moduleType} #{note.moduleId}
                </span>
                <span className="block text-gray-500" data-id="element-18801">{note.moduleName}</span>
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-18802">
              <dt className="text-sm font-medium text-gray-500" data-id="element-18803">Note Type</dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-18804">
                {note.noteTypeName}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-18805">
              <dt className="text-sm font-medium text-gray-500" data-id="element-18806">Author</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-18807">
                <UserIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-18808" />
                {note.authorName}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-18809">
              <dt className="text-sm font-medium text-gray-500" data-id="element-18810">Visibility</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-18811">
                <EyeIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-18812" />
                {note.visibility}
                {note.visibilityDetails && <span className="ml-1 text-gray-500" data-id="element-18813">
                    ({note.visibilityDetails})
                  </span>}
              </dd>
            </div>
            {(note.entityName || note.siteName) && <div className="sm:col-span-2" data-id="element-18814">
                <dt className="text-sm font-medium text-gray-500" data-id="element-18815">Location</dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-18816">
                  {note.entityName && <span className="mr-2" data-id="element-18817">Entity: {note.entityName}</span>}
                  {note.siteName && <span data-id="element-18818">Site: {note.siteName}</span>}
                </dd>
              </div>}
            <div className="sm:col-span-2" data-id="element-18819">
              <dt className="text-sm font-medium text-gray-500" data-id="element-18820">
                Note Content
              </dt>
              <dd className="mt-1 text-sm text-gray-900 p-3 bg-gray-50 rounded border border-gray-200 whitespace-pre-wrap" data-id="element-18821">
                {note.body}
              </dd>
            </div>
            {note.tags && note.tags.length > 0 && <div className="sm:col-span-2" data-id="element-18822">
                <dt className="text-sm font-medium text-gray-500" data-id="element-18823">Tags</dt>
                <dd className="mt-1" data-id="element-18824">
                  <div className="flex flex-wrap gap-2" data-id="element-18825">
                    {note.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-18826">
                        <TagIcon className="h-3 w-3 mr-1" data-id="element-18827" />
                        {tag}
                      </span>)}
                  </div>
                </dd>
              </div>}
            {note.hasAttachment && <div className="sm:col-span-2" data-id="element-18828">
                <dt className="text-sm font-medium text-gray-500" data-id="element-18829">
                  Attachment
                </dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-18830">
                  <div className="flex items-center" data-id="element-18831">
                    <FileIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-18832" />
                    <span data-id="element-18833">{note.attachmentName}</span>
                    <button className="ml-2 inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-18834">
                      <DownloadIcon className="h-3 w-3 mr-1" data-id="element-18835" />
                      Download
                    </button>
                  </div>
                </dd>
              </div>}
          </dl>
        </div>
      </div>
      {/* Note history */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-18836">
        <div className="px-4 py-5 sm:px-6" data-id="element-18837">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-18838">
            Note History
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-18839">
            Audit trail of changes to this note.
          </p>
        </div>
        <div className="border-t border-gray-200" data-id="element-18840">
          <ul className="divide-y divide-gray-200" data-id="element-18841">
            {history.length === 0 ? <li className="px-4 py-4 sm:px-6 text-center text-sm text-gray-500" data-id="element-18842">
                No history available for this note.
              </li> : history.map(entry => <li key={entry.id} className="px-4 py-4 sm:px-6" data-id="element-18843">
                  <div className="flex items-center justify-between" data-id="element-18844">
                    <div className="flex items-center" data-id="element-18845">
                      <ClockIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-18846" />
                      <div data-id="element-18847">
                        <div className="flex items-center" data-id="element-18848">
                          {renderActionBadge(entry.action)}
                          <span className="ml-2 text-sm text-gray-500" data-id="element-18849">
                            {formatTimestamp(entry.timestamp)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-18850">
                          By: {entry.userName}
                        </div>
                        {entry.details && <div className="mt-1 text-sm text-gray-500" data-id="element-18851">
                            {entry.details}
                          </div>}
                      </div>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>
    </div>;
}